import type { GetStaticProps, NextPage } from "next";
import { Controller, useForm } from "react-hook-form";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import DatePicker, { registerLocale } from "react-datepicker";
import enNZ from "date-fns/locale/en-NZ";
import { useFormspark } from "@formspark/use-formspark";
import Page from "@/components/Page";
import {
  forwardRef,
  FunctionComponent,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useState,
} from "react";

// import {
//   CakeTextInput as TextInput,
//   CakeSelect as Select,
// } from "@/components/rhf-components";

registerLocale("en-NZ", enNZ);

import { CakeRequestState } from "types";
import TextInput from "@/components/TextInput";
import SimpleSelect from "@/components/SimpleSelect";
import Modal from "@/components/Dialog";
import Thanks from "@/components/Thanks";

const Flavours = ["Chocolate", "Vanilla", "Banana", "Red Velvet"];

type CakeRequestProps = {
  dates: {
    min: string;
    max: string;
    invalid: string[];
  };
};

const dayFormatter = new Intl.DateTimeFormat("sv-SV");
const formatter = new Intl.DateTimeFormat("en-NZ", {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
});

const birthdays = [
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
  { value: 5, label: "5th" },
  { value: 6, label: "6th" },
  { value: 7, label: "7th" },
  { value: 8, label: "8th" },
  { value: 9, label: "9th" },
  { value: 10, label: "10th" },
  { value: 11, label: "11th" },
  { value: 12, label: "12th" },
  { value: 13, label: "13th" },
  { value: 14, label: "14th" },
  { value: 15, label: "15th" },
  { value: 16, label: "16th" },
  { value: 17, label: "17th" },
  { value: 18, label: "18th" },
];

const FORM_ID = process.env.NEXT_PUBLIC_FORMSPARK_FORM_ID || "test";

export const getStaticProps: GetStaticProps = async (context) => {
  // ...
  const minDate = new Date(Date.now() + 12096e5);
  const maxDate = new Date(minDate);
  maxDate.setMonth(minDate.getMonth() + 10);

  const invalid: string[] = [
    // test
    "2022-07-08",
    "2022-07-09",
    "2022-07-10",
    "2022-07-11",
    "2022-07-12",
    "2022-07-13",
    "2022-07-14",
    "2022-07-15",
    "2022-07-16",
    "2022-07-27",

    // public holidays
    "2022-10-24",
    "2022-12-25",
    "2022-12-26",
    "2022-12-27",
    "2023-01-01",
    "2023-01-02",
    "2023-01-03",
    "2023-02-06",
    "2023-04-07",
    "2023-04-25",
    "2023-06-05",
    "2023-07-14",
    "2023-10-23",
    "2023-12-25",
    "2023-12-26",

    // sweetpeas closed
  ];

  return {
    props: {
      dates: {
        min: dayFormatter.format(minDate),
        max: dayFormatter.format(maxDate),
        invalid,
      },
    },
  };
};

const fields = {
  dateSubmitted: "Submitted On",
  firstName: "Name of Caregiver",
  lastName: "Email",
  phone: "Phone",
  collectionDate: "Date Cake Required",
  theme: "Theme of cake",
  flavour: "Flavour of cake",
  childName: "First name of child optional",
  referral: "How did you hear about The Cake Detective",
  agency: "Name of Agency",
  notes: "Additional information",
  allergies: "Allergies etc",
  area: "area ie hamilton morrinsville etc",
  age: "age of child on their up coming birthday",
  privacy: "privacy statement",
};

type Status = "ready" | "submitting" | "success" | "error";

type NumberResponse = {
  number: number;
  reference: string;
};

type CustomInputProps = {
  value: string;
  onClick: MouseEventHandler<HTMLInputElement>;
};

// eslint-disable-next-line react/display-name
const CollectionDateInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ value, onClick }, ref) => (
    <input
      ref={ref}
      type="text"
      id="collection-date"
      required
      value={value}
      onClick={onClick}
      readOnly
      aria-describedby={`collection-date-description`}
    />
  )
);

const CakeRequestForm: NextPage<CakeRequestProps> = ({ dates }) => {
  const [submit, submitting] = useFormspark({ formId: FORM_ID });

  const [status, setStatus] = useState<Status>("ready");
  const minDate = new Date(Date.now() + 12096e5);
  const maxDate = new Date(minDate);
  maxDate.setMonth(minDate.getMonth() + 10);

  const {
    control,
    watch,
    register,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<CakeRequestState>();

  const excludeDate = useCallback(
    (date) => {
      const day = dayFormatter.format(date);

      return dates.invalid.includes(day);
    },
    [dates]
  );

  const onSubmit = useCallback(
    async (data: CakeRequestState) => {
      // show submitting dialog
      try {
        setStatus("submitting");
        // get a request number
        const response = await fetch(`/api/request_number`);
        const parsedResponse: NumberResponse = await response.json();
        const { number, reference } = parsedResponse;
        if (!response.ok) {
          throw "Could not submit request at this time.";
        }

        await submit({
          ...data,
          number,
          reference,
          collectionDate: dayFormatter.format(data.collectionDate),
          collectionDateFormatted: formatter.format(data.collectionDate),
          birthday: `${birthdays[Number(data.age) - 1].label} Birthday Cake`,
        });

        // show success dialog
        // - go to home
        // - make another request
        setStatus("success");
      } catch {
        // show error dialog
        setStatus("error");
      }
    },
    [setStatus, submit]
  );

  const watchCollectionDate = watch("collectionDate");

  const resetForMultiple = () => {
    // only reset the fields that are likely to change
    window.location.reload();
  };

  return (
    <Page title="Cake Request Form - The Cake Detective">
      <Page.Header />
      <Page.Content>
        <div className="lg:mx-8">
          <div className="lg:mx-auto lg:max-w-7xl lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-12">
            <div className="py-8 max-w-xl mx-auto col-start-1 lg:py-32 lg:max-w-none lg:mx-0 lg:px-0 lg:col-start-1">
              <h2 className="text-base text-ranga-base font-semibold tracking-wide uppercase">
                The Cake Detective
              </h2>
              <h3 className="mt-2 mb-6 text-3xl leading-8 font-extrabold tracking-tight text-navy-base sm:text-4xl capitalize">
                Cake Request Form
              </h3>
              <p className="mt-1 mb-12 text-sm text-navy-500">
                All information entered in this form is private and won&apos;t
                be shared publicly
              </p>
              <form
                noValidate
                className="space-y-8 divide-y divide-gray-200"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="space-y-8 divide-y divide-ranga-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-semibold uppercase text-ranga-base tracking-wide">
                        Refferring Agency
                      </h3>
                      <p className="mt-2 text-sm text-navy-500">
                        Right now, you need to be referred by one of the
                        wonderful agencies we work with in the Waikato.
                      </p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div className="sm:col-start-1 sm:col-end-3">
                        <TextInput
                          label="Agency you were referred by"
                          type="text"
                          required
                          error={errors["agency"]?.message}
                          {...register("agency", {
                            required:
                              "Please enter the agency you were referred by",
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-semibold uppercase text-ranga-base tracking-wide">
                        Cake Collection
                      </h3>
                      <p className="mt-1 text-sm text-navy-500 max-w-2xl">
                        Choose a date close to when the cake will be eaten.
                        Cakes last up to 3 days if refrigerated and can be
                        collected Monday to Saturday (excluding public holidays)
                        from Sweet Pea Parties in Rostrevor Street, Hamilton.
                      </p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div className="sm:col-start-1 sm:col-end-2">
                        <label
                          htmlFor="collection-date"
                          className="block text-sm mb-1 font-medium text-navy-500"
                        >
                          Collection Date
                        </label>
                        <Controller
                          control={control}
                          name="collectionDate"
                          rules={{
                            validate: {
                              validDate: (date) =>
                                !!date &&
                                date?.getDay() !== 0 &&
                                !excludeDate(date),
                            },
                            required: "Please choose a collection date",
                          }}
                          render={({ field: { onChange, value } }) => (
                            <DatePicker
                              id="collection-date"
                              locale="en-NZ"
                              selected={value}
                              onChange={onChange}
                              required
                              wrapperClassName={
                                errors["collectionDate"] ? "error" : "test"
                              }
                              customInput={
                                <CollectionDateInput
                                  value={dayFormatter.format(value)}
                                  onClick={(e) => e.currentTarget.focus()}
                                />
                              }
                              startDate={null}
                              nextMonthButtonLabel={
                                <ChevronRightIcon className="h-4 w-4" />
                              }
                              previousMonthButtonLabel={
                                <ChevronLeftIcon className="h-4 w-4" />
                              }
                              dateFormat="dd/MM/yyyy"
                              minDate={minDate}
                              maxDate={maxDate}
                              filterDate={(date: Date) =>
                                date.getDay() !== 0 && !excludeDate(date)
                              }
                            />
                          )}
                        />
                        {errors["collectionDate"] ? (
                          <p
                            className="mt-2 pl-0.5 text-sm text-red-800 opacity-75"
                            id={`collectionDate-error`}
                          >
                            {errors["collectionDate"].message}
                          </p>
                        ) : null}
                        {watchCollectionDate && (
                          <p
                            className="mt-2 text-sm text-navy-500 opacity-75 pl-0.5"
                            id={`collection-date-description`}
                          >
                            Pick up {formatter.format(watchCollectionDate)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-semibold uppercase text-ranga-base tracking-wide">
                        Cake Details
                      </h3>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-4">
                      <div className="sm:col-span-1">
                        <SimpleSelect
                          label="Birthday"
                          options={birthdays}
                          {...register("age", { required: true })}
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <TextInput
                          label="First name of child"
                          type="text"
                          {...register("childName")}
                          help="Optional"
                        />
                      </div>
                      <div className="sm:col-span-4">
                        <TextInput
                          label="Cake Theme"
                          type="text"
                          required
                          error={errors["theme"]?.message}
                          {...register("theme", {
                            required: "Please enter a theme for the cake",
                          })}
                          help="Something your child loves or enjoys e.g. characters, sports, animals"
                        />
                      </div>
                      <div className="sm:col-span-4">
                        <div>
                          <label className="text-sm font-medium text-gray-900">
                            Flavour*
                          </label>
                          <fieldset className="mt-4">
                            <legend className="sr-only">Flavour of cake</legend>
                            <div className="space-y-4">
                              {Flavours.map((option) => (
                                <div key={option} className="flex items-center">
                                  <input
                                    id={option}
                                    type="radio"
                                    value={option}
                                    {...register("flavour", {
                                      required: "Please choose a flavour",
                                    })}
                                    className="focus:ring-teal-500 h-4 w-4 text-teal-600 border-gray-300"
                                  />
                                  <label
                                    htmlFor={option}
                                    className="ml-3 block text-sm font-medium text-gray-700"
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </fieldset>
                          {errors["flavour"] ? (
                            <p
                              className="mt-2 pl-0.5 text-sm text-red-800 opacity-75"
                              id={`flavour-error`}
                            >
                              {errors["flavour"].message}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="sm:col-span-4">
                        <TextInput
                          label="Allergies"
                          type="text"
                          {...register("allergies")}
                          help="Optional"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-semibold uppercase text-ranga-base tracking-wide">
                        Contact Details
                      </h3>
                      <p className="mt-2 text-sm text-navy-500">
                        These should be the details of the person co-ordinating
                        the cake (usually the person filling out this form). We
                        will get in touch with this person to confirm we have
                        received the request.
                      </p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div className="sm:col-span-1">
                        <TextInput
                          label="First Name"
                          type="text"
                          required
                          error={errors["firstName"]?.message}
                          {...register("firstName", {
                            required: "Please enter your first name",
                          })}
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <TextInput
                          label="Last Name"
                          type="text"
                          required
                          error={errors["lastName"]?.message}
                          {...register("lastName", {
                            required: "Please enter your last name",
                          })}
                        />
                      </div>
                      <div className="sm:col-start-1 sm:col-end-3">
                        <TextInput
                          label="Email"
                          type="text"
                          required
                          error={errors["email"]?.message}
                          {...register("email", {
                            required: "Please enter an email address",
                          })}
                        />
                      </div>
                      <div className="sm:col-start-1 sm:col-span-1">
                        <TextInput
                          label="Phone"
                          type="text"
                          required
                          error={errors["phone"]?.message}
                          {...register("phone", {
                            required: "Please enter a phone number",
                          })}
                        />
                      </div>
                      <div className="sm:col-start-1 sm:col-end-3">
                        <TextInput
                          label="Area of the Waikato"
                          type="text"
                          required
                          error={errors["area"]?.message}
                          {...register("area", {
                            required:
                              "Please enter the area of the Waikato the child resides",
                          })}
                          help="e.g. Hamilton, Huntly, Morrinsville, etc"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-semibold uppercase text-ranga-base tracking-wide">
                        Additional Information
                      </h3>
                      <p className="mt-1 text-sm text-navy-500 max-w-2xl">
                        Please let us know any other relevant details or
                        questions you may have.
                      </p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div className="sm:col-start-1 sm:col-end-3">
                        <textarea
                          className="shadow-sm focus:ring-teal-500 focus:border-teal-500 block w-full sm:text-sm border-ranga-300 rounded-md"
                          {...register("notes")}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div className="mb-4">
                      <h3 className="text-lg leading-6 font-semibold uppercase text-ranga-base tracking-wide">
                        Privacy Statement
                      </h3>
                      <p className="mt-1 text-sm text-navy-500 max-w-2xl">
                        We collect personal information from you, including
                        information about your: name, contact information,
                        location. We collect your personal information in order
                        to: have the information required to donate cakes.
                        Providing some information is optional. If you choose
                        not to enter contact information, we&apos;ll be unable
                        to provide our services.
                      </p>
                      <p className="mt-1 text-sm text-navy-500 max-w-2xl">
                        You have the right to ask for a copy of any personal
                        information we hold about you, and to ask for it to be
                        corrected if you think it is wrong. If you&apos;d like
                        to ask for a copy of your information, or to have it
                        corrected, please contact us at
                        info@thecakedetective.com, or 02102640373, or 14 Longbow
                        Terrace, Flagstaff, Hamilton.
                      </p>
                    </div>
                    <div className="relative flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="privacy"
                          aria-describedby="privacy-description"
                          type="checkbox"
                          {...register("privacy")}
                          className="focus:ring-teal-500 h-4 w-4 text-teal-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label
                          htmlFor="privacy"
                          className="font-medium text-navy-500"
                        >
                          I have read the privacy statement
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div className="flex items-center justify-between">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                      >
                        {submitting ? "Submitting..." : "Submit Request"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <Thanks />
        {(submitting || status === "submitting" || status === "success") && (
          <Modal
            initialOpen
            onClose={resetForMultiple}
            isLoading={submitting || status === "submitting"}
          >
            <span>
              We have received your request. Someone will be in touch to confirm
              your request.
            </span>
          </Modal>
        )}
      </Page.Content>
      <Page.Footer />
    </Page>
  );
};

export default CakeRequestForm;
